import styled from 'styled-components';
import { ITrackingCreatorContentDB } from 'type/tracking';

import { useGetTrackingCreatorContents } from 'hooks/queries/tracking';
import CoreImg from 'shared/CoreImg';
import CoreLoading from 'shared/CoreLoading';
import CoreSvg from 'shared/CoreSvg';
import Icon from 'shared/icons';
import style from 'styles/styled-components/style';

const CONTENT_TYPE_ICON: Record<string, JSX.Element> = {
	ig_feed: <Icon.Insta />,
	ig_video: <Icon.InstaReels />,
	yt_video: <Icon.Youtube />,
	yt_shorts: <Icon.YoutubeShorts />,
};

interface ICreatorContentsAccordion {
	code: string;
	influencerCode: string;
	username?: string;
	contentCount?: number;
}

const formatDate = (dateStr: string) => {
	if (!dateStr) return '';
	const d = new Date(dateStr);
	if (isNaN(d.getTime())) return '';
	const yy = String(d.getFullYear()).slice(2);
	const mm = String(d.getMonth() + 1).padStart(2, '0');
	const dd = String(d.getDate()).padStart(2, '0');
	return `${yy}.${mm}.${dd}`;
};

const CreatorContentsAccordion = ({
	code,
	influencerCode,
	username,
	contentCount,
}: ICreatorContentsAccordion) => {
	const { data: contents, isLoading } = useGetTrackingCreatorContents(
		code,
		influencerCode,
	);

	if (isLoading) {
		return (
			<AccordionWrapper>
				<Grid flex="center" _height="100px">
					<CoreLoading color_type="black" size_type="small" />
				</Grid>
			</AccordionWrapper>
		);
	}

	if (!contents?.length) {
		return (
			<AccordionWrapper>
				<Grid flex="center" _height="60px">
					<Text typo="span" color="gray">
						콘텐츠가 없습니다.
					</Text>
				</Grid>
			</AccordionWrapper>
		);
	}

	return (
		<AccordionWrapper>
			{username && (
				<AccordionHeader>
					<Text typo="span_bold">{username}</Text>
					<Text typo="span" color="gray">
						{' '}
						콘텐츠 {contents.length}개
					</Text>
				</AccordionHeader>
			)}
			{contents.map((item: ITrackingCreatorContentDB, idx: number) => (
				<ContentRow
					key={`${item.contentLink}-${idx}`}
					onClick={() => window.open(item.contentLink, '_blank', 'noopener,noreferrer')}
				>
					<Thumbnail>
						{CONTENT_TYPE_ICON[item.contentType] && (
							<ContentTypeIcon>
								<CoreSvg _size="12px">{CONTENT_TYPE_ICON[item.contentType]}</CoreSvg>
							</ContentTypeIcon>
						)}
						<CoreImg
							src={item.thumbnailUrl}
							alt=""
							_width={40}
							_height={40}
							error_src="https://featuring-score-web.s3.amazonaws.com/static/images/score/v3/common/cmmn-default-image.png"
							lazy
						/>
					</Thumbnail>
					<ContentInfo>
						<Description>{item.contentTitle ?? item.contentDescription}</Description>
						<DateLabel>{formatDate(item.uploadDate)}</DateLabel>
					</ContentInfo>
					<MetricGroup>
						<MetricItem>
							<MetricLabel>조회수</MetricLabel>
							<MetricValue>{item.viewCountsDisabled ? '비공개' : (item.viewCount ?? 0).toLocaleString()}</MetricValue>
						</MetricItem>
						<MetricItem>
							<MetricLabel>좋아요</MetricLabel>
							<MetricValue>{item.likeCountsDisabled ? '비공개' : (item.likeCount ?? 0).toLocaleString()}</MetricValue>
						</MetricItem>
						<MetricItem>
							<MetricLabel>댓글</MetricLabel>
							<MetricValue>{item.commentsDisabled ? '비공개' : (item.commentCount ?? 0).toLocaleString()}</MetricValue>
						</MetricItem>
						<MetricItem>
							<MetricLabel>공유</MetricLabel>
							<MetricValue>{(item.shareCount ?? 0).toLocaleString()}</MetricValue>
						</MetricItem>
					</MetricGroup>
				</ContentRow>
			))}
		</AccordionWrapper>
	);
};

const AccordionWrapper = styled.div`
	background-color: ${({ theme }) => theme.colors.gray.gray05_bg};
	border-bottom: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	padding: 8px 16px 8px 60px;
`;

const AccordionHeader = styled.div`
	padding: 8px 0 4px;
`;

const ContentRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 10px 0;
	border-bottom: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	cursor: pointer;

	&:last-child {
		border-bottom: none;
	}

	&:hover {
		opacity: 0.8;
	}
`;

const Thumbnail = styled.div`
	position: relative;
	width: 40px;
	height: 40px;
	border-radius: 6px;
	overflow: hidden;
	flex-shrink: 0;
`;

const ContentTypeIcon = styled.div`
	position: absolute;
	top: 2px;
	left: 2px;
	width: 16px;
	height: 16px;
	border-radius: 3px;
	background: rgba(255, 255, 255, 0.9);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1;
`;

const ContentInfo = styled.div`
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
`;

const Description = styled.div`
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray80_text};
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
`;

const DateLabel = styled.div`
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray50};
`;

const MetricGroup = styled.div`
	display: flex;
	gap: 16px;
	flex-shrink: 0;
`;

const MetricItem = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 2px;
`;

const MetricLabel = styled.span`
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray50};
	font-size: 11px;
`;

const MetricValue = styled.span`
	${({ theme }) => theme.typo.body.span_bold};
	color: ${({ theme }) => theme.colors.gray.gray80_text};
`;

const { Grid, Text } = style;

export default CreatorContentsAccordion;
